/* eslint-disable @shopify/jsx-no-hardcoded-content */
import { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  MAIN_NAVIGATION,
  NavigationItem,
  SEARCH_MENU_KEY,
} from '../constants/navigation';
import { generateLabel } from '../lib/utility';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'contexts/AuthContext';
import { config } from 'config';
import { useOnboardingProgress } from './useOnboardingProgress';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { Flex, MenuProps, Tag } from '@robinpowered/ui-kit';
import styled from '@emotion/styled';

const SearchMenuButton = styled.button`
  all: unset;
  position: relative;
  display: block;
  width: 100%;
  cursor: pointer;
  color: inherit;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
  }
`;

// Helper function to strip matchMode from navigation items before passing to Ant Design Menu
const stripMatchMode = (item: NavigationItem): NavigationItem => {
  const { matchMode, ...rest } = item;
  if (item.children) {
    return {
      ...rest,
      children: item.children.map(stripMatchMode),
    };
  }
  return rest;
};

export const useNavigation = () => {
  const { t } = useTranslation('globalNavigation');
  const { userPermissions, currentOrg } = useAuthContext();
  const permissions = userPermissions;
  const location = useLocation();
  const onboardingProgress = useOnboardingProgress();
  const { checkFlag } = useFeatureFlags();

  // Extract feature flags and computed values
  const features = useMemo(
    () => ({
      showManageTab:
        permissions.canManageAmenities ||
        permissions.canViewBilling ||
        permissions.canViewCheckpoints ||
        permissions.canViewDevices ||
        permissions.canViewOfficeAccess ||
        permissions.canManageGroups ||
        permissions.canManagePlaces ||
        permissions.canManageRoles ||
        permissions.canManageUsers ||
        permissions.canManageWorkplaceServices ||
        permissions.canManageWorkPolicies,

      showPeopleTab:
        currentOrg?.hasActiveLocations &&
        permissions.canListUsers &&
        currentOrg?.features?.peopleDirectory,

      showInsightsTab:
        currentOrg?.hasActiveLocations && permissions.canViewAnalytics,

      showWorkWeek:
        permissions.canViewWorkweek && currentOrg?.features?.workweek,

      showSpaceManagementTab:
        permissions.canManagePlaces || permissions.canManageScenarioPlanning,

      showManageBeta: checkFlag('hydra(setup-manage-beta)', false),
    }),
    [permissions, currentOrg, checkFlag]
  );

  // Extract settings page default logic
  const settingsPageDefault = useMemo(() => {
    if (permissions.canManagePlaces) return 'offices';
    if (permissions.canViewOfficeAccess) return 'access';
    if (permissions.canViewCheckpoints) return 'integrations';
    if (permissions.canViewBilling) return 'billing';
    if (permissions.canViewDevices) return 'devices';
    if (permissions.canManageUsers) return 'users';
    if (permissions.canManageGroups) return 'groups';
    if (permissions.canManageRoles) return 'roles';
    if (permissions.canManageWorkplaceServices) return 'services';
    if (permissions.canManageAmenities) return 'amenities';
    if (permissions.canManageWorkPolicies) return 'work-policies';
    return 'customization';
  }, [permissions]);

  const gettingStartedLink = useMemo(() => {
    if (
      !currentOrg?.slug ||
      !permissions.canManagePlaces ||
      onboardingProgress?.allComplete
    ) {
      return undefined;
    }
    return `/${currentOrg.slug}/setup/dashboard`;
  }, [
    currentOrg?.slug,
    permissions.canManagePlaces,
    onboardingProgress?.allComplete,
  ]);

  // Extract navigation item processing logic
  const processNavigationItem = useMemo(() => {
    // Visibility configuration - maps item keys to their visibility check functions.
    // Align with common/header HeaderMenu: only show items when the user has permission
    // to view the linked page (schedule, people, analytics, visits, deliveries, tickets, manage, etc.).
    const visibilityChecks: Record<string, () => boolean> = {
      'mySchedule.title': () => !!currentOrg?.features?.workweek,
      people: () => !!features.showPeopleTab,
      analytics: () => !!features.showInsightsTab,
      'workplace.dashboard': () => !!permissions.canManageWorkplaceServices,
      'workplace.visitors': () => !!permissions.canViewVisitTab,
      'workplace.deliveries': () => !!permissions.canViewDeliveriesTab,
      'workplace.tickets': () => !!permissions.canManageWorkplaceServices,
      'spaceManagement.title': () => features.showSpaceManagementTab,
      'spaceManagement.scenarioPlanning': () =>
        !!permissions.canManageScenarioPlanning,
      'spaceManagement.layoutTool': () => !!permissions.canManagePlaces,
    };

    return (item: NavigationItem): NavigationItem | null => {
      // Check visibility before processing
      const visibilityCheck = visibilityChecks[item.key];
      if (visibilityCheck && !visibilityCheck()) {
        return null;
      }

      // Item processors: only for items that need non-trivial route or label transforms.
      // Simple show/hide is handled by visibilityChecks above.
      const itemProcessors: Record<string, () => NavigationItem | null> = {
        // Global search has no route — render its label as a button. Clicking it
        // opens the search overlay (handled in NavSidebar via onOpenSearch).
        [SEARCH_MENU_KEY]: () => ({
          ...item,
          label: (
            <SearchMenuButton type="button">{t('search')}</SearchMenuButton>
          ),
        }),

        // Route varies based on workweek feature flag
        'mySchedule.title': () => {
          const route = `/schedule/${features.showWorkWeek ? 'week' : 'user'}`;
          const isExternal = !features.showWorkWeek;
          const labelRoute = currentOrg?.slug
            ? isExternal
              ? `${config.dashboardUrl}/${currentOrg.slug}${route}`
              : `/${currentOrg.slug}${route}`
            : route;

          return {
            ...item,
            route,
            label: generateLabel({
              label: t(item.key),
              route: labelRoute,
              isExternal,
            }),
          };
        },

        // Route differs from the static value in navigation.tsx (/analytics → /insights)
        analytics: () => {
          const route = '/insights';
          return {
            ...item,
            route,
            label: generateLabel({
              label: t(item.key),
              route: currentOrg?.slug ? `/${currentOrg.slug}${route}` : route,
            }),
          };
        },

        manage: () => {
          if (!features.showManageTab || !currentOrg?.slug) return null;
          const route = `/settings/${settingsPageDefault}`;
          const fullRoute = `${config.dashboardUrl}/${currentOrg.slug}${route}`;
          return {
            ...item,
            route: fullRoute,
            label: generateLabel({
              label: t(item.key),
              route: fullRoute,
              isExternal: true,
            }),
          };
        },

        manageBeta: () => {
          if (
            !features.showManageBeta ||
            !features.showManageTab ||
            !currentOrg?.slug
          )
            return null;
          const route = `/setup/settings`;
          const fullRoute = `/${currentOrg.slug}${route}`;
          return {
            ...item,
            route,
            label: (
              <Flex align="center" gap={6}>
                <Link to={fullRoute}>{t('manage')}</Link>
                <Tag
                  bordered={true}
                  color="blue"
                  style={{
                    fontSize: '11px',
                    lineHeight: '16px',
                    padding: '0 4px',
                    marginInlineEnd: 0,
                  }}
                >
                  beta
                </Tag>
              </Flex>
            ),
          };
        },
      };

      // Process item if it has a specific processor
      if (itemProcessors[item.key]) {
        return itemProcessors[item.key]();
      }

      // Process children recursively
      if (item.children) {
        const processedChildren = item.children
          .map(processNavigationItem)
          .filter((child): child is NavigationItem => child !== null)
          .map((child) => ({
            ...child,
            // Process the child label here
            label: generateLabel({
              label: t(child.key),
              route: child.route ? `/${currentOrg?.slug}${child.route}` : '',
            }),
          }));

        if (processedChildren.length === 0) {
          return null; // Hide parent if no children can be visible
        }

        return {
          ...item,
          // Process the parent label here
          label: t(item.key),
          children: processedChildren,
        };
      }

      // For regular items without children, process the label
      return {
        ...item,
        label: generateLabel({
          label: t(item.key),
          route: item.route ? `/${currentOrg?.slug}${item.route}` : '',
        }),
      };
    };
  }, [
    t,
    currentOrg?.slug,
    currentOrg?.features?.workweek,
    permissions.canManagePlaces,
    permissions.canManageScenarioPlanning,
    permissions.canViewVisitTab,
    permissions.canViewDeliveriesTab,
    permissions.canManageWorkplaceServices,
    features.showWorkWeek,
    features.showPeopleTab,
    features.showInsightsTab,
    features.showSpaceManagementTab,
    features.showManageTab,
    features.showManageBeta,
    settingsPageDefault,
  ]);

  const navigation = useMemo(() => {
    return MAIN_NAVIGATION.map(processNavigationItem).filter(
      (item): item is NavigationItem => item !== null
    );
  }, [processNavigationItem]);

  const menuItems = useMemo<MenuProps['items']>(
    () => navigation.map(stripMatchMode),
    [navigation]
  );

  // Determine active keys and open keys based on current pathname
  const { activeKeys, openKeys } = useMemo(() => {
    const currentPathname = location.pathname;
    const activeKeys: Set<string> = new Set();
    const openKeys: Set<string> = new Set();

    const findActiveKeys = (items: NavigationItem[], parentKey?: string) => {
      items.forEach((item) => {
        if (item.route && currentOrg?.slug) {
          const fullRoute = `/${currentOrg?.slug}${item.route}`;
          const matchMode = item.matchMode ?? 'inclusive';

          let isMatch = false;
          if (matchMode === 'exact') {
            // Exact match: only match the exact route or route with trailing slash
            isMatch =
              currentPathname === fullRoute ||
              currentPathname === `${fullRoute}/`;
          } else {
            // Inclusive match: check if the route is included in the current pathname
            isMatch = currentPathname.includes(fullRoute);
          }

          if (isMatch) {
            activeKeys.add(item.key);
            if (parentKey) {
              activeKeys.add(parentKey);
              openKeys.add(parentKey);
            }
          }
        }

        if (item.children) {
          findActiveKeys(item.children, item.key);
        }
      });
    };

    findActiveKeys(navigation);

    if (
      currentOrg?.slug &&
      location.pathname.includes(`/${currentOrg.slug}/setup/dashboard`)
    ) {
      activeKeys.add('finishSetup');
    }

    return {
      activeKeys: Array.from(activeKeys),
      openKeys: Array.from(openKeys),
    };
  }, [navigation, currentOrg?.slug, location.pathname]);

  return {
    menuItems,
    activeKeys,
    openKeys,
    gettingStartedLink,
    onboardingProgress,
  };
};
